import React, { Component } from 'react';
import { connect } from 'react-redux';
import { injectIntl, FormattedMessage } from 'react-intl';
import { bindActionCreatorsSafe } from 'util/safeDispatch';

import BrowseServerFoldersDialog from 'view/App/DialogPanels/BrowseServerFoldersDialog';
import localizedStrings from 'util/localizedStrings';
import ServerLocationRow from './ServerLocationRow';
import {
  getActivePanelId,
  getGlobalSettings,
  getLoggingConfig,
  getServerStatus,
} from 'state/selectors';
import type {
  Dispatch,
  GlobalSettings,
  LoggingConfig,
  ServerStatus,
} from 'state/types';
import { showDialogPanel } from 'state/commands/dialogPanelCommands';
import {
  setGlobalSettings,
  setLoggingConfig,
} from 'state/commands/adminPropertiesCommands';
import { actions as modalPromptActions } from 'portfolio-shared/state/reducers/modalPromptReducer';
import {
  BROWSE_SERVER_FOLDERS_PANEL_ID,
  SERVER_STATE_STOPPED,
} from 'util/constants';
import { GLOBAL_SETTING } from 'util/serverUtils';
const { showModalPrompt } = modalPromptActions;

type Props = {
  activePanelId?: string,
  getActivePanelId: typeof getActivePanelId,
  globalSettings: GlobalSettings,
  loggingConfig: LoggingConfig,
  serverStatus: ServerStatus,
  setGlobalSettings: Function,
  setLoggingConfig: Function,
  showDialogPanel: typeof showDialogPanel,
  showModalPrompt: typeof showModalPrompt,
};

type State = {
  activeBrowsingFolder?: string,
};

class ServerLocationsPage extends Component<Props, State> {
  handleBrowseForDirectory: Function;
  handlePathSelected: Function;
  handleToggleCheckbox: Function;
  updateGlobalSetting: Function;

  constructor() {
    super();
    this.handleBrowseForDirectory = this.handleBrowseForDirectory.bind(this);
    this.handlePathSelected = this.handlePathSelected.bind(this);
    this.handleToggleCheckbox = this.handleToggleCheckbox.bind(this);
    this.updateGlobalSetting = this.updateGlobalSetting.bind(this);
  }
  handleBrowseForDirectory(event, folder: string) {
    this.setState({ activeBrowsingFolder: folder });
    this.props.showDialogPanel(BROWSE_SERVER_FOLDERS_PANEL_ID);
  }
  handlePathSelected(path) {
    this.updateGlobalSetting(this.state.activeBrowsingFolder, path);
    this.setState({ activeBrowsingFolder: undefined });
  }
  handleToggleCheckbox(event, key: string) {
    const newValue = this.props.globalSettings[key] === 'false';
    this.updateGlobalSetting(key, newValue);
  }
  async updateGlobalSetting(name: string, value) {
    const { setGlobalSettings, setLoggingConfig, showModalPrompt } = this.props;
    if (name === GLOBAL_SETTING.FOLDER_LOGS) {
      setLoggingConfig({
        ...this.props.loggingConfig,
        logDirectory: value,
      });
    } else {
      await setGlobalSettings({ [name]: value });
      if (name === GLOBAL_SETTING.FOLDER_WORK) {
        showModalPrompt({
          message:
            localizedStrings.GLOBAL_SETTINGS_WORK_DIRECTORY_REQUIRES_RESTART_MSG,
        });
      }
    }
  }
  render() {
    const {
      activePanelId,
      globalSettings,
      loggingConfig,
      serverStatus,
    } = this.props;
    const isStopped = serverStatus.runState === SERVER_STATE_STOPPED;
    return (
      <div className="page page-contents" data-test-name="data-locations-page">
        <header className="page-header">
          <FormattedMessage
            {...localizedStrings.GLOBAL_SETTINGS_SERVER_DATA_LOCATIONS}
          />
        </header>

        <div className="section page-contents-textured portfolio-form-container">
          <div className="box">
            <ServerLocationRow
              checkboxChecked={
                globalSettings[GLOBAL_SETTING.CREATE_AUTOSYNC_FOLDER] === 'true'
              }
              checkboxKey={GLOBAL_SETTING.CREATE_AUTOSYNC_FOLDER}
              disabled={isStopped}
              folderKey={GLOBAL_SETTING.FOLDER_AUTOSYNC}
              handleChangeLocation={this.handleBrowseForDirectory}
              handleToggleCheckbox={this.handleToggleCheckbox}
              label={localizedStrings.GLOBAL_SETTINGS_AUTOSYNC_PATH_LABEL}
              path={globalSettings[GLOBAL_SETTING.FOLDER_AUTOSYNC]}
              showCheckbox={true}
            />
            <hr />
            <ServerLocationRow
              defaultPath={
                globalSettings[GLOBAL_SETTING.FOLDER_PREVIEWS_DEFAULT]
              }
              disabled={isStopped}
              folderKey={GLOBAL_SETTING.FOLDER_PREVIEWS}
              handleChangeLocation={this.handleBrowseForDirectory}
              label={localizedStrings.GLOBAL_SETTINGS_PREVIEW_PATH_LABEL}
              path={globalSettings[GLOBAL_SETTING.FOLDER_PREVIEWS]}
            />
            <hr />
            <ServerLocationRow
              defaultPath={globalSettings[GLOBAL_SETTING.FOLDER_WORK_DEFAULT]}
              folderKey={GLOBAL_SETTING.FOLDER_WORK}
              handleChangeLocation={this.handleBrowseForDirectory}
              label={localizedStrings.GLOBAL_SETTINGS_WORK_DIRECTORY_LABEL}
              path={globalSettings[GLOBAL_SETTING.FOLDER_WORK]}
            />
            <hr />
            <ServerLocationRow
              defaultPath={globalSettings[GLOBAL_SETTING.FOLDER_VAULT_DEFAULT]}
              disabled={!isStopped}
              folderKey={GLOBAL_SETTING.FOLDER_VAULT}
              handleChangeLocation={this.handleBrowseForDirectory}
              label={localizedStrings.GLOBAL_SETTINGS_VAULT_DIRECTORY_LABEL}
              path={globalSettings[GLOBAL_SETTING.FOLDER_VAULT]}
            />
            <hr />
            <ServerLocationRow
              defaultPath={globalSettings[GLOBAL_SETTING.FOLDER_LOGS_DEFAULT]}
              disabled={!isStopped}
              folderKey={GLOBAL_SETTING.FOLDER_LOGS}
              handleChangeLocation={this.handleBrowseForDirectory}
              label={localizedStrings.LOGGING_LOG_DIR}
              path={loggingConfig.logDirectory}
            />
          </div>
        </div>

        {activePanelId === BROWSE_SERVER_FOLDERS_PANEL_ID && (
          <BrowseServerFoldersDialog
            onPathSelected={this.handlePathSelected}
            showFiles={false}
          />
        )}
      </div>
    );
  }
}

export default connect(
  state => ({
    activePanelId: getActivePanelId(state),
    globalSettings: getGlobalSettings(state),
    loggingConfig: getLoggingConfig(state),
    serverStatus: getServerStatus(state),
  }),
  (dispatch: Dispatch) =>
    bindActionCreatorsSafe({
      getActivePanelId,
      setGlobalSettings,
      setLoggingConfig,
      showDialogPanel,
      showModalPrompt,
    })
)(injectIntl(ServerLocationsPage));



// WEBPACK FOOTER //
// ./src/view/Pages/GlobalSettingsPages/ServerLocationsPage.js